/** Copyright (C) 2022 Trend Micro Inc. All rights reserved. */

// @ts-nocheck

export const c1AccountsInitState = {
  requestAccounts: false,
  hasError: false,
  hasInitialAccountId: false,
  c1AccountID: '',
  c1AccountsList: [],
  lastAccessedAccountID: '',
  fetchRecInvitations: false,
  invitationsReceived: [],
  showInitialInvitationsPage: true,
  urlHasInvitationID: false,
}

export const c1ServicesInitState = {
  hasError: false,
  fetchServices: false,
  c1ServicesList: [],
  errorMsg: '',
}

export const c1TokenInitState = {
  token: '',
  expires: '',
  hasError: false,
  tokenIsRevoked: false,
  fetchC1Token: false,
  userUrn: '',
  role: '',
  roleName: '',
}

export const c1UserInfoInitState: C1CRedux.C1UserDetails = {
  name: '',
  email: '',
  hasError: false,
  type: '',
  timezone: '',
  wsAdminID: null,
  errorMsg: '',
}

export const commonInitState = {
  storeId: 'c1-microfrontend-fss',
  appSelection: '',
  wsLoggedIn: false, // WS session state during dual sign-in phase
  showOldBanner: true, // show blue banner by default during dual sign-in phase
  loggedIn: false,
  loggingOut: false,
  isLoading: false,
  hasError: false,
  locale: 'en',
  errorMsg: '',
  theme: { colorMode: 'light', overrides: {} },
}

export function c1Common(state = commonInitState, action) {
  switch (action.type) {
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState.c1Common,
        storeId: commonInitState.storeId,
      }
    case 'NAVIGATE_APP':
      return {
        ...state,
        appSelection: action.appName,
      }
    case 'WS_LOGGED_IN':
      return {
        ...state,
        wsLoggedIn: true,
      }
    case 'WS_LOGGED_OUT':
      return {
        ...state,
        wsLoggedIn: false,
      }
    case 'LOGGED_IN':
      return {
        ...state,
        loggedIn: true,
        loggingOut: false,
      }
    case 'LOGGING_OUT':
      return {
        ...state,
        loggedIn: false,
        loggingOut: true,
      }
    case 'SHOW_LOADING_INDICATOR':
    case 'HIDE_LOADING_INDICATOR': {
      return {
        ...state,
        isLoading: action.loading,
      }
    }
    case 'SET_LOCALE':
      return {
        ...state,
        locale: action.locale,
      }
    case 'SET_THEME':
      return {
        ...state,
        theme: action.theme,
      }
    case 'SET_COLOR_MODE':
      return {
        ...state,
        theme: {
          ...state.theme,
          colorMode: action.colorMode,
        },
      }
    case 'SET_THEME_OVERRIDES':
      return {
        ...state,
        theme: {
          ...state.theme,
          overrides: action.overrides,
        },
      }
    case 'TOGGLE_OLD_BANNER': {
      return {
        ...state,
        showOldBanner: action.showOldBanner,
      }
    }
    case 'RESET_STORE':
      return {
        ...state,
        ...commonInitState,
        showOldBanner: state.showOldBanner,
      }
    default:
      return state
  }
}

export const c1Accounts = (state = c1AccountsInitState, action) => {
  switch (action.type) {
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState.c1Accounts,
      }
    case 'FETCH_ACCOUNTS':
      return {
        ...state,
        requestAccounts: action.requestAccounts,
      }
    case 'ADD_LAST_SELECTED_ACCOUNT_ID':
      return {
        ...state,
        hasInitialAccountId: true,
        lastAccessedAccountID: action.c1AccountID,
        c1AccountID: action.c1AccountID,
      }
    case 'ADD_ACCOUNT_ID':
    case 'FETCH_BY_ACCOUNT_ID':
      return {
        ...state,
        hasInitialAccountId: false,
        lastAccessedAccountID: state.c1AccountID,
        c1AccountID: action.c1AccountID,
      }
    case 'ADD_ACCOUNTS':
    case 'STORE_ACCOUNTS':
      return {
        ...state,
        hasInitialAccountId: false,
        c1AccountsList: action.storeAccounts,
        hasError: false,
        errorMsg: '',
        requestAccounts: false,
      }

    case 'ERROR_FETCHING_ACCOUNTS':
      return {
        ...state,
        ...c1AccountsInitState,
        hasError: true,
        requestAccounts: false,
        fetchRecInvitations: false,
        errorMsg: action.errorMsg,
      }
    case 'RESET_STORE':
      return {
        ...state,
        ...c1AccountsInitState,
      }
    case 'STORE_RECEIVED_INVITATIONS':
      return {
        ...state,
        hasError: false,
        fetchRecInvitations: false,
        invitationsReceived: action.invitationsReceived,
      }
    // showInitialInvitationsPage flag is used to avoid redirecting users back to invitations page
    // if user clicks on Create New Account button on my invitations page without accepting or rejecting any invitation
    case 'SHOW_INITIAL_INVITATION_PAGE': {
      return {
        ...state,
        showInitialInvitationsPage: action.showInitialInvitationsPage,
      }
    }
    case 'FETCH_RECEIVED_INVITATIONS': {
      return {
        ...state,
        fetchRecInvitations: action.fetchRecInvitations,
      }
    }
    case 'URL_HAS_INVITATION_ID': {
      return {
        ...state,
        urlHasInvitationID: action.urlHasInvitationID,
      }
    }

    default:
      return state
  }
}

export const c1Services = (state = c1ServicesInitState, action) => {
  switch (action.type) {
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState.c1Services,
      }
    case 'FETCH_SERVICES':
      return {
        ...state,
        fetchServices: action.fetchServices,
      }
    case 'STORE_SERVICES':
      return {
        ...state,
        c1ServicesList: action.c1ServicesList,
        hasError: false,
        errorMsg: '',
        fetchServices: false,
      }
    case 'ERROR_FETCHING_SERVICES':
      return {
        ...state,
        ...c1ServicesInitState,
        hasError: true,
        errorMsg: action.errorMsg,
      }
    case 'RESET_STORE':
      return {
        ...state,
        ...c1ServicesInitState,
      }
    default:
      return state
  }
}

export const c1UserDetails = (state = c1UserInfoInitState, action) => {
  switch (action.type) {
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState.c1UserDetails,
      }
    case 'ADD_USER_INFO':
      const { name = '', email = '' } = action.userDetails
      return {
        ...state,
        name,
        email,
        hasError: false,
        errorMsg: '',
      }
    case 'ERROR_ADDING_USER_INFO':
      return {
        ...state,
        hasError: true,
        errorMsg: action.errorMsg,
      }
    case 'SET_WS_ADMIN_ID':
      return {
        ...state,
        wsAdminID: action.wsAdminID,
      }
    case 'ADD_USER_TYPE':
      return {
        ...state,
        type: action.userType,
      }
    case 'RESET_STORE':
      return {
        ...state,
        ...c1UserInfoInitState,
      }
    default:
      return state
  }
}

export const c1Token = (state = c1TokenInitState, action) => {
  switch (action.type) {
    case 'FETCH_C1_TOKEN':
      return {
        ...state,
        fetchC1Token: action.fetchC1Token,
      }
    case 'STORE_C1_TOKEN':
      return {
        ...state,
        ...action.c1Token,
        hasError: false,
        fetchC1Token: false,
        errorMsg: '',
      }
    case 'C1_TOKEN_REVOKED_STATUS':
      return {
        ...state,
        tokenIsRevoked: action.tokenIsRevoked,
      }
    case 'ERROR_FETCHING_C1_TOKEN':
      return {
        ...state,
        ...c1TokenInitState,
        hasError: true,
        errorMsg: action.errorMsg,
      }
    case 'RESET_STORE':
      return {
        ...state,
        ...c1TokenInitState,
      }
    default:
      return state
  }
}
